import { ManifestationStep, SavedAffirmation } from "../types";

interface StepPrompt {
  message: string[];
  followUp?: string[];
}

export const stepPrompts: Record<ManifestationStep, StepPrompt> = {
  "welcome": {
    message: [
      "Welcome to your manifestation journey! I'm here to guide you through a proven process for bringing your desires into reality.",
      "Welcome to Mindmail! I'll help you manifest your desires through a powerful process.",
      "Welcome to your manifestation journey! Let's bring your desires into reality.",
      "Hi there! I'm here to guide you through the manifestation process.",
      "Welcome! Ready to transform your desires into reality?"
    ],
    followUp: [
      "Would you like to use an existing affirmation or create a new one?\n(Type: existing or new)",
      "Start with a saved affirmation or create a new one?\n(Type: existing or new)",
      "Should we use one of your saved affirmations or create something new?\n(Type: existing or new)",
      "Existing affirmation or new one?\n(Type: existing or new)",
      "Would you prefer to use a saved affirmation or create a new one?\n(Type: existing or new)"
    ]
  },
  "affirmation-choice": {
    message: [
      "Awesome! Let's create your new affirmation.",
      "Great choice! Let's craft your new affirmation.",
      "Perfect! Time to create your new affirmation.",
      "Excellent! Let's develop your new affirmation.",
      "Wonderful! Let's shape your new affirmation."
    ],
    followUp: [
      "What's something you'd like to manifest in your life? Share a clear goal or desire.",
      "What goal would you like to manifest? Make it something exciting and meaningful.",
      "What would you love to achieve? Write it as a clear goal that excites you.",
      "Share a goal that's meaningful to you - something you truly desire.",
      "Tell me a goal that matters to you - something exciting you'd love to manifest."
    ]
  },
  "goal-confirmation": {
    message: [
      "Perfect goal! Now let's phrase it in the present tense."
    ],
    followUp: [
      "Please write out your goal in present tense in a way that suits you. This will form your manifestation statement for the process to follow. Rewrite your goal \"{goal}\" as if it's already happening right now. For example, you could say: \"{presentTenseExample}\".",
      "Please write out your goal in present tense in a way that suits you. This will form your manifestation statement for the process to follow. Turn \"{goal}\" into a present tense statement, as if it's already true. For example: \"{presentTenseExample}\".",
      "Please write out your goal in present tense in a way that suits you. This will form your manifestation statement for the process to follow. Rewrite \"{goal}\" in present tense, as if it's happening now. This makes your affirmation more powerful. For example: \"{presentTenseExample}\".",
      "Please write out your goal in present tense in a way that suits you. This will form your manifestation statement for the process to follow. Now, state \"{goal}\" as if it's already your reality. This present tense form connects you to the feeling of success. For example: \"{presentTenseExample}\".",
      "Please write out your goal in present tense in a way that suits you. This will form your manifestation statement for the process to follow. Write \"{goal}\" as if it's already happened. For example: \"{presentTenseExample}\"."
    ]
  },
  "goal-setting": {
    message: [
      "Perfect! That's a powerful present tense affirmation.",
      "That's a great present tense affirmation!",
      "Beautiful! Your affirmation is now in present tense.",
      "Excellent! That's a powerful present tense statement.",
      "Perfect! Your present tense affirmation is ready."
    ],
    followUp: [
      "How many cycles would you like to go through today? (Pick a number between 1 and 12)\n\nℹ️ *A cycle is one round of writing, feeling, and visualizing your affirmation.*",
      "How many manifestation cycles would you like to complete today? (Choose 1-12)\n\nℹ️ *Each cycle is one pass through writing, feeling, and visualizing - repetition deepens the connection.*",
      "Select how many cycles you want to practice (1-12)\n\nℹ️ *A cycle = one round of writing out your affirmation, connecting with the feeling, and visualizing your life as if it's real.*",
      "How many cycles would you like to go through? (1-12)\n\nℹ️ *Each cycle takes you through writing, feeling, and visualizing your affirmation - more cycles, deeper alignment.*",
      "Choose how many manifestation cycles to complete (1-12)\n\nℹ️ *A cycle is one loop of affirming, feeling, and visualizing your goal as already achieved.*"
    ]
  },
  "cycle-setup": {
    message: [
      "Perfect! We'll go through {totalCycles} powerful, focused cycles. Each one will help you align your energy with your desire.",
      "Great! {totalCycles} cycles will help align your energy with your desire.",
      "Excellent! {totalCycles} cycles will strengthen your manifestation practice.",
      "Perfect! We'll complete {totalCycles} cycles to anchor your desire.",
      "Wonderful! {totalCycles} cycles will help bring your affirmation to life."
    ],
    followUp: [
      "Cycle {currentCycle}\nPlease write out your full affirmation below, exactly as shown, then press **Enter**:\n\n\"{presentTenseGoal}\"\n\n*Try to feel it as if it's already true as you write it - use Enter to submit it into your subconscious mind.*",
      "Cycle {currentCycle}\nWrite out your affirmation in full below, word for word, then hit **Enter**:\n\n\"{presentTenseGoal}\"\n\n*Feel it as already real as you type - pressing Enter anchors it into your subconscious.*",
      "Cycle {currentCycle}\nType your affirmation out in full below, exactly as it appears, and press **Enter** to submit:\n\n\"{presentTenseGoal}\"\n\n*Let yourself feel it as true while you write - Enter plants it in your subconscious mind.*",
      "Cycle {currentCycle}\nPlease write the full affirmation below, just as shown, then press **Enter**:\n\n\"{presentTenseGoal}\"\n\n*Feel it as if it's already your reality as you write - Enter sends it into your subconscious.*",
      "Cycle {currentCycle}\nWrite your affirmation out in full below, exactly as written, then press **Enter**:\n\n\"{presentTenseGoal}\"\n\n*Feel it as already true as the words flow - Enter drops it into your subconscious mind.*"
    ]
  },
  "present-tense": {
    message: [
      "Perfect! Now let's deepen the manifestation process.",
      "Great! Now let's connect with the emotional aspect.",
      "Excellent! Let's explore how this feels.",
      "Beautiful! Now let's bring this to life emotionally.",
      "Wonderful! Let's connect with the feeling of this reality."
    ],
    followUp: [
      "👉 How does it FEEL when \"{presentTenseGoal}\" is your reality?\n\nWhat emotions flow through you? Really take a moment to connect.",
      "👉 What's the vibe inside you when \"{presentTenseGoal}\" is simply true?\n\nSit with it for a beat before you answer.",
      "👉 What softens or lights up in you now that \"{presentTenseGoal}\"?\n\nName the shift, however small.",
      "👉 What's the steady undercurrent of feeling in this reality where \"{presentTenseGoal}\"?\n\nNot the peak - the baseline.",
      "👉 As \"{presentTenseGoal}\" is now your reality, what feeling anchors it?\n\nThe one that would still be there tomorrow morning."
    ]
  },
  "feeling-state": {
    message: [
      "Fantastic - let's go deeper still.",
      "Those feelings are powerful! Let's go even deeper.",
      "Beautiful! Now let's make this even more vivid.",
      "Wonderful feelings! Let's bring this to life.",
      "Excellent! Now let's immerse ourselves fully."
    ],
    followUp: [
      "What do you notice around you in this reality? What do you hear? Who's around you and how do you feel?",
      "Look around in this manifested reality - what do you see? What sounds are present? Who's with you?",
      "Describe the scene around you. What does your environment look like? Who's there, and what are they saying?",
      "Paint the picture of this reality - what's your surroundings like? What conversations are happening? How do you carry yourself?",
      "Step into this reality fully - what do you notice? What details stand out? Who's sharing this moment with you?"
    ]
  },
  "respond": {
    message: [
      "Fantastic - now let's consider the gap.",
      "Beautiful visualization! Now let's check in.",
      "That's a vivid picture! One more reflection.",
      "Wonderful! Now let's ground this.",
      "Perfect! One more important step."
    ],
    followUp: [
      "🌟 On a scale of 1 to 10, how real does this manifestation feel right now?\n\n✨ This awareness helps us bridge the gap between your current reality and the one you're creating.",
      "🌟 Rate from 1 to 10 - how real and present does \"{presentTenseGoal}\" feel to you right now?\n\n✨ This insight helps dissolve any barriers between you and your manifestation.",
      "🌟 On a scale of 1-10, how close does this feel? 1 being distant, 10 being completely real and true.\n\n✨ Naming this gap is the first step to closing it.",
      "🌟 From 1 to 10, how real does \"{presentTenseGoal}\" feel right now?\n\n✨ This honest check-in accelerates your manifestation.",
      "🌟 On a scale of 1-10, how tangible and real does this reality feel to you right now?\n\n✨ This awareness is a powerful catalyst for manifestation."
    ]
  },
  "reality-check": {
    message: [
      "Got it - thanks for the honest read.",
      "Noted. That gives us a real starting point.",
      "Good check-in.",
      "That number tells me a lot.",
      "Appreciate the honesty on that one."
    ],
    followUp: [
      "Ready for the next step?",
      "Shall we continue?",
      "Ready to move forward?",
      "Want to keep going?",
      "Ready for what's next?"
    ]
  },
  "summary": {
    message: [
      "Thank you for sharing! This awareness is powerful - it shows exactly where we need to focus.",
      "Your insight is valuable! This awareness of where you are helps bridge the gap.",
      "This self-awareness is perfect - seeing both where you are and where you're going creates the path.",
      "Thank you for your honesty. This clarity helps dissolve the space between desire and manifestation.",
      "Your reflection shows you're deeply engaged with this process. This awareness is transformative."
    ],
    followUp: [
      "Now that you've completed all {totalCycles} cycles...\n\n✨ What is the predominant emotion you're feeling right now - as if your manifestation is already real?",
      "After these {totalCycles} powerful cycles...\n\n✨ What single emotion stands out the most right now, as you sit with the feeling that this is already your reality?",
      "Having completed all {totalCycles} cycles...\n\n✨ If you had to name the one dominant feeling you're experiencing right now - feeling as though your desire is already fulfilled - what would it be?",
      "With all {totalCycles} cycles complete...\n\n✨ What is the strongest emotion present for you right now, as you hold the feeling that this manifestation is already real?",
      "Now that you've finished all {totalCycles} cycles...\n\n✨ What predominant emotion are you feeling in this moment, as if your affirmation has already come true?"
    ]
  },
  
  "core-feeling": {
    message: [
      "Beautiful! Feeling {coreFeeling} is powerful manifestation energy.",
      "What you've named, \"{coreFeeling}\", is exactly what attracts your desire.",
      "Holding \"{coreFeeling}\" is precisely the vibration that creates your manifestation.",
      "That sense of \"{coreFeeling}\" is the bridge to your desired reality.",
      "What you're feeling, \"{coreFeeling}\", is the magnetic force drawing your manifestation to you."
    ],
    followUp: [
      "Now, one final powerful reflection:\n\n🌟 What experiences in your current life already leave you feeling \"{coreFeeling}\"?\nThe more you focus on these, the more you multiply the vibration that attracts your desire.",
      "For our final step:\n\n🌟 Where in your current reality do you already feel \"{coreFeeling}\"?\nConnecting with these moments accelerates your manifestation.",
      "Let's complete your manifestation practice:\n\n🌟 What currently leaves you feeling \"{coreFeeling}\"?\nRecognizing these bridges helps your manifestation come faster.",
      "One last powerful step:\n\n🌟 When do you already feel \"{coreFeeling}\" in your daily life?\nThese moments are your manifestation anchors.",
      "To complete this practice:\n\n🌟 What already leaves you feeling \"{coreFeeling}\" right now?\nThese are the seeds of your expanding reality."
    ]
  },
  
  "current-reality": {
    message: [
      "Perfect! You've completed the manifestation bridge - connecting your desire with what already exists.",
      "Beautiful! Recognizing where you already feel \"{coreFeeling}\" accelerates your manifestation.",
      "Excellent! These current moments of feeling \"{coreFeeling}\" are creating a bridge to your desired reality.",
      "Wonderful! Those moments of feeling \"{coreFeeling}\" are powerful manifestation catalysts.",
      "Perfect! Already feeling \"{coreFeeling}\" is a direct connection to your manifestation."
    ],
    followUp: [
      "Would you like to begin another manifestation process?",
      "Your manifestation is in motion. Would you like to start a new journey?",
      "The process is complete. Would you like to begin again?",
      "You've done powerful work today. Start another manifestation?",
      "This completes your manifestation practice. Begin a new one?"
    ]
  },
  "save-prompt": {
    message: [
      "Would you like to save this affirmation for future sessions?"
    ],
    followUp: [
      "Type 'yes' to save or 'no' to skip."
    ]
  },
  "completed": {
    message: [
      "Session complete! ✨"
    ]
  }
};

export const formatAffirmationOptions = (affirmations: SavedAffirmation[]): string => {
  if (affirmations.length === 0) return "";
  
  let options = "You can choose from your previous affirmations:\n\n";
  
  affirmations.forEach((aff, index) => {
    options += `${index + 1}. "${aff.text}"\n`;
  });
  
  options += "\nPlease select an affirmation by number, type 'new' to create one, or type 'reset' to clear all saved affirmations.";
  
  return options;
};

export const getNextStep = (currentStep: ManifestationStep, isLastCycle: boolean = false): ManifestationStep => {
  const steps: ManifestationStep[] = [
    'welcome',
    'affirmation-choice',
    'goal-confirmation',
    'goal-setting',
    'cycle-setup',
    'present-tense',
    'feeling-state',
    'respond',
    'reality-check',
    'summary',
    'core-feeling',
    'current-reality',
    'save-prompt'
  ];
  
  const currentIndex = steps.indexOf(currentStep);
  
  if (currentIndex === -1) {
    return 'welcome';
  }
  
  if (currentStep === 'summary' && !isLastCycle) {
    return 'cycle-setup';
  }
  
  if (currentStep === 'summary' && isLastCycle) {
    return 'core-feeling';
  }
  
  if (currentStep === 'current-reality') {
    return 'save-prompt';
  }
  
  if (currentStep === 'save-prompt') {
    return 'welcome';
  }
  
  if (currentIndex < steps.length - 1) {
    return steps[currentIndex + 1];
  }
  
  return 'welcome';
};

export const getRandomMessage = (messages: string[]): string => {
  if (!messages || messages.length === 0) return "";
  const randomIndex = Math.floor(Math.random() * messages.length);
  return messages[randomIndex];
};

export const rephraseToPresentTense = (goal: string): string => {
  let rephrased = goal.trim();
  
  // Remove trailing punctuation for processing
  rephrased = rephrased.replace(/[.!]+$/, '').trim();
  
  // Common patterns to strip, including typos like "I've like to" (meant as "I'd like to")
  const futurePatterns = [
    /^I(?:'d| would) (?:love|like) to\s+/i,
    /^I(?:'ve| have) (?:love|like) to\s+/i,  // common typo
    /^I want to\s+/i,
    /^I hope to\s+/i,
    /^I wish to\s+/i,
    /^I(?:'d| would) (?:love|like) for\s+/i,
    /^I(?:'m| am) (?:going|trying|planning|hoping) to\s+/i,
    /^I plan to\s+/i,
    /^I need to\s+/i,
    /^I want\s+/i,
    /^my goal is to\s+/i,
    /^I dream of\s+/i,
    /^I'd love\s+/i,
  ];
  
  for (const pattern of futurePatterns) {
    if (pattern.test(rephrased)) {
      const core = rephrased.replace(pattern, '').trim();
      return buildPresentTense(core);
    }
  }
  
  // If it starts with "I" followed by a future-sounding verb, try to extract the action
  const genericIPattern = /^I\s+(?:want|wish|hope|plan|intend|aim)\s+/i;
  if (genericIPattern.test(rephrased)) {
    const core = rephrased.replace(genericIPattern, '').trim();
    return buildPresentTense(core);
  }
  
  // Fallback: rephrase more naturally
  return buildPresentTense(rephrased);
};

const buildPresentTense = (core: string): string => {
  // Clean up the core phrase
  let clean = core.trim();
  
  // Map verbs to past-tense "already accomplished" form + a natural follow-up
  const actionVerbs: Record<string, { past: string; followUp: string }> = {
    'launch': { past: 'launched', followUp: "and it's going well" },
    'build': { past: 'built', followUp: "and it's thriving" },
    'create': { past: 'created', followUp: "and it's flourishing" },
    'start': { past: 'started', followUp: "and it's going great" },
    'grow': { past: 'grown', followUp: "beyond my expectations" },
    'make': { past: 'made', followUp: "and I'm proud of it" },
    'become': { past: 'become', followUp: "and it feels amazing" },
    'get': { past: 'gotten', followUp: "and I'm grateful for it" },
    'have': { past: 'achieved', followUp: "and it feels wonderful" },
    'find': { past: 'found', followUp: "and it brings me joy" },
    'earn': { past: 'earned', followUp: "and I'm grateful for it" },
    'achieve': { past: 'achieved', followUp: "and it feels incredible" },
    'reach': { past: 'reached', followUp: "and I'm celebrating" },
    'live': { past: 'living', followUp: "and I love every moment" },
    'travel': { past: 'traveled', followUp: "and it's been wonderful" },
    'move': { past: 'moved', followUp: "and it feels like home" },
    'write': { past: 'written', followUp: "and I'm proud of it" },
    'finish': { past: 'finished', followUp: "and it turned out great" },
    'complete': { past: 'completed', followUp: "and I'm proud of the result" },
    'learn': { past: 'learned', followUp: "and I'm applying it daily" },
    'master': { past: 'mastered', followUp: "and it feels empowering" },
    'develop': { past: 'developed', followUp: "and it's making an impact" },
    'improve': { past: 'improved', followUp: "and the results are showing" },
    'save': { past: 'saved', followUp: "and I feel financially secure" },
    'run': { past: 'running', followUp: "successfully" },
    'open': { past: 'opened', followUp: "and it's thriving" },
    'sell': { past: 'sold', followUp: "and it exceeded my goals" },
    'win': { past: 'won', followUp: "and I'm celebrating" },
    'land': { past: 'landed', followUp: "and it's everything I wanted" },
    'publish': { past: 'published', followUp: "and it's reaching people" },
    'release': { past: 'released', followUp: "and it's been well received" },
    'see': { past: 'seen', followUp: "and it's been eye-opening" },
    'do': { past: 'done', followUp: "and I'm proud of myself" },
    'lose': { past: 'lost', followUp: "and I feel healthier than ever" },
    'pay': { past: 'paid off', followUp: "and I feel free" },
    'quit': { past: 'quit', followUp: "and I feel liberated" },
    'buy': { past: 'bought', followUp: "and I'm enjoying it" },
    'be': { past: 'become', followUp: "and it feels wonderful" },
    'feel': { past: 'found', followUp: "and it's my natural state" },
    'attract': { past: 'attracted', followUp: "and I'm grateful" },
    'manifest': { past: 'manifested', followUp: "and it's my reality now" },
    'transform': { past: 'transformed', followUp: "and the change is remarkable" },
    'overcome': { past: 'overcome', followUp: "and I feel stronger for it" },
    'embrace': { past: 'embraced', followUp: "and it enriches my life" },
  };
  
  const firstWord = clean.split(/\s+/)[0].toLowerCase();
  
  if (actionVerbs[firstWord]) {
    const rest = clean.slice(firstWord.length).trim();
    const { past, followUp } = actionVerbs[firstWord];
    return `I've successfully ${past} ${rest}, ${followUp}`;
  }
  
  // If it already sounds like a present statement, keep it
  if (/^(I am|I have|I've|my|the)/i.test(clean)) {
    return clean;
  }
  
  // Fallback: frame as accomplished
  return `I've successfully ${clean.charAt(0).toLowerCase() + clean.slice(1)}, and it feels amazing`;
};
